// CB:BEGIN admin-priority2-performance-controls-component
/**
 * Performance Controls Component
 * Allows admins to set ratings and performance tiers for contractors
 */
import React, { useState, useEffect } from 'react';
import { useApi } from '../../lib/useApi';

interface PerformanceControlsProps {
  contractorId: string;
  onSuccess: () => void;
}

interface PerformanceSnapshot {
  id: string;
  createdAt: string;
  totalCompletedProjects: number;
  averageRating: number | null;
}

interface AdminAction {
  id: string;
  action: string;
  payload: any;
  createdAt: string;
  actorUserId: string | null;
}

export const PerformanceControls: React.FC<PerformanceControlsProps> = ({
  contractorId,
  onSuccess
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Rating state
  const [rating, setRating] = useState(5);
  const [ratingReason, setRatingReason] = useState('');
  const [ratingNotes, setRatingNotes] = useState('');

  // Tier state
  const [tier, setTier] = useState('STANDARD');
  const [tierReason, setTierReason] = useState('');

  // History state
  const [performanceHistory, setPerformanceHistory] = useState<{
    contractor: any;
    performanceSnapshots: PerformanceSnapshot[];
    adminActions: AdminAction[];
  } | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const handleSetRating = async () => {
    if (!ratingReason.trim()) {
      alert('Rating reason required');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/admin/contractors/${contractorId}/admin-rating`, {
        rating,
        reason: ratingReason,
        notes: ratingNotes || null
      });
      alert(`✅ Admin rating set to ${rating}/5`);
      setShowRatingModal(false);
      setRatingReason('');
      setRatingNotes('');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to set rating'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSetTier = async () => {
    if (!tierReason.trim()) {
      alert('Tier change reason required');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/admin/contractors/${contractorId}/performance-tier`, {
        tier,
        reason: tierReason
      });
      alert(`✅ Performance tier set to ${tier}`);
      setShowTierModal(false);
      setTierReason('');
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to set performance tier'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = async () => {
    setShowHistoryModal(true);
    setHistoryLoading(true);
    try {
      const data = await api.get(`/api/admin/contractors/${contractorId}/performance-history`);
      setPerformanceHistory(data);
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Failed to load performance history'}`);
      setShowHistoryModal(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <>
      <div style={{
        padding: '16px',
        background: '#f0fdf4',
        borderRadius: '8px',
        marginBottom: '16px',
        border: '1px solid #86efac'
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#166534' }}>
          Performance Management
        </h3>
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#15803d' }}>
          Set admin ratings and performance tiers for this contractor
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowRatingModal(true)}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#f59e0b',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Set Admin Rating
          </button>

          <button
            onClick={() => setShowTierModal(true)}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#8b5cf6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Set Performance Tier
          </button>

          <button
            onClick={handleViewHistory}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            View Performance History
          </button>
        </div>
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowRatingModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Set Admin Rating
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Rating: {rating}/5
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {[1, 2, 3, 4, 5].map(r => (
                  <button
                    key={r}
                    onClick={() => setRating(r)}
                    style={{
                      width: '40px',
                      height: '40px',
                      border: '2px solid',
                      borderColor: rating >= r ? '#f59e0b' : '#d1d5db',
                      borderRadius: '6px',
                      background: rating >= r ? '#fef3c7' : '#fff',
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: rating >= r ? '#f59e0b' : '#6b7280'
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <p style={{ margin: '0', fontSize: '12px', color: '#6b7280' }}>
                1 = Poor, 5 = Excellent
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Reason (Required)
              </label>
              <textarea
                value={ratingReason}
                onChange={(e) => setRatingReason(e.target.value)}
                placeholder="Why are you setting this rating?"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Additional Notes (Optional)
              </label>
              <textarea
                value={ratingNotes}
                onChange={(e) => setRatingNotes(e.target.value)}
                placeholder="Optional additional context"
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRatingModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSetRating}
                disabled={loading || !ratingReason.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading || !ratingReason.trim() ? '#9ca3af' : '#f59e0b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !ratingReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Setting...' : 'Set Rating'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tier Modal */}
      {showTierModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowTierModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Set Performance Tier
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Performance Tier
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { value: 'PREMIUM', label: 'Premium', desc: 'Top performers, prioritized in bid matching', color: '#10b981' },
                  { value: 'STANDARD', label: 'Standard', desc: 'Normal standing, no restrictions', color: '#3b82f6' },
                  { value: 'PROBATION', label: 'Probation', desc: 'Under review, may have restrictions', color: '#ef4444' }
                ].map(t => (
                  <button
                    key={t.value}
                    onClick={() => setTier(t.value)}
                    style={{
                      padding: '12px',
                      border: '2px solid',
                      borderColor: tier === t.value ? t.color : '#d1d5db',
                      borderRadius: '8px',
                      background: tier === t.value ? `${t.color}20` : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{ fontSize: '16px', fontWeight: '600', color: t.color, marginBottom: '4px' }}>
                      {t.label}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {t.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Reason (Required)
              </label>
              <textarea
                value={tierReason}
                onChange={(e) => setTierReason(e.target.value)}
                placeholder="Why are you changing this tier?"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTierModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSetTier}
                disabled={loading || !tierReason.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading || !tierReason.trim() ? '#9ca3af' : '#8b5cf6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !tierReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Setting...' : 'Set Tier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Performance History Modal */}
      {showHistoryModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowHistoryModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Performance History
            </h3>

            {historyLoading ? (
              <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading...</p>
            ) : performanceHistory ? (
              <>
                {/* Performance Snapshots */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                    Performance Snapshots
                  </h4>
                  {performanceHistory.performanceSnapshots.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {performanceHistory.performanceSnapshots.map(snapshot => (
                        <div
                          key={snapshot.id}
                          style={{
                            padding: '12px',
                            background: '#f9fafb',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>
                              {new Date(snapshot.createdAt).toLocaleDateString()}
                            </span>
                            <span style={{ fontSize: '14px', color: '#6b7280' }}>
                              {snapshot.totalCompletedProjects} projects completed
                            </span>
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            Average Rating: {snapshot.averageRating ? snapshot.averageRating.toFixed(1) : 'N/A'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>No performance snapshots recorded</p>
                  )}
                </div>

                {/* Admin Actions */}
                <div>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                    Admin Actions
                  </h4>
                  {performanceHistory.adminActions.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {performanceHistory.adminActions.map(action => (
                        <div
                          key={action.id}
                          style={{
                            padding: '12px',
                            background: '#fef3c7',
                            borderRadius: '6px',
                            border: '1px solid #fbbf24'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                              {action.action.replace(/_/g, ' ').toUpperCase()}
                            </span>
                            <span style={{ fontSize: '13px', color: '#78350f' }}>
                              {new Date(action.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          {action.payload && (
                            <div style={{ fontSize: '13px', color: '#78350f', marginTop: '8px' }}>
                              {action.payload.rating && <div>Rating: {action.payload.rating}/5</div>}
                              {action.payload.tier && <div>Tier: {action.payload.tier}</div>}
                              {action.payload.reason && <div>Reason: {action.payload.reason}</div>}
                              {action.payload.notes && <div>Notes: {action.payload.notes}</div>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>No admin actions recorded</p>
                  )}
                </div>
              </>
            ) : (
              <p style={{ color: '#6b7280' }}>Failed to load history</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={() => setShowHistoryModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
// CB:END admin-priority2-performance-controls-component
